import React, { Component } from "react";
import ReactDOM from "react-dom";
import * as Icon from "react-feather";
import clsx from "clsx";

import "./filter-chip.css";

// Popover sizing. The list shows whole rows only, so nothing is ever clipped mid-item.
const ROW_H = 44;
const LIST_ROWS = 6;
const MENU_MAX_H = 460;
const VIEWPORT_GUTTER = 12;

// A searchable list with thousands of entries renders only this many until the user narrows it
const MAX_RENDERED_OPTIONS = 150;

/**
 * A filter trigger with a popover list underneath it.
 *
 * The popover is portaled to <body> and positioned from the trigger's bounding rect, so it is never clipped by
 * the table's scroll container. The parent owns the open state so only one popover is open at a time.
 *
 * Props:
 *   variant        : "chip" (dashed filter pill, default) | "button" (toolbar button) | "pill" (status pill inside a row)
 *   tone           : for the pill variant, the status id used for colouring
 *   label          : trigger text
 *   labelClassName : extra class on the label span (functional test hooks)
 *   icon           : optional leading icon node
 *   count          : number of selected items, shown as a badge when > 0
 *   active         : highlights the trigger when a filter is applied
 *   open, onOpen, onClose
 *   title          : heading shown at the top of the popover
 *   searchable, searchPlaceholder
 *   options        : [{ id, label, meta?, checked?, dot?, avatar?, tone? }]
 *   sections       : [{ title, options }] (alternative to options)
 *   single         : radio behaviour, the parent closes the popover after a pick
 *   onToggle(option), onSelectAll(), onClear()
 *   extra          : node rendered under the list (for example a "Custom range" button)
 *   align          : "left" | "right"
 *   minWidth       : popover width in px
 */
class FilterChip extends Component {
	state = { query: "", menuStyle: null, listMaxHeight: ROW_H * LIST_ROWS, openUp: false };

	wrapRef = React.createRef();
	_menuEl = null;

	componentDidMount() {
		if (this.props.open) {
			this._bind();
		}
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.open && this.props.open) {
			this._bind();
		}

		if (prevProps.open && !this.props.open) {
			this._unbind();

			if (this.state.query) {
				this.setState({ query: "" });
			}
		}
	}

	componentWillUnmount() {
		this._unbind();
	}

	_bind() {
		this._position();
		document.addEventListener("mousedown", this._onDocMouseDown, true);
		window.addEventListener("scroll", this._position, true);
		window.addEventListener("resize", this._position);
	}

	_unbind() {
		document.removeEventListener("mousedown", this._onDocMouseDown, true);
		window.removeEventListener("scroll", this._position, true);
		window.removeEventListener("resize", this._position);
	}

	_onDocMouseDown = event => {
		const wrap = this.wrapRef.current;

		if (wrap && wrap.contains(event.target)) {
			return;
		}

		if (this._menuEl && this._menuEl.contains(event.target)) {
			return;
		}

		if (this.props.onClose) {
			this.props.onClose();
		}
	};

	_position = () => {
		const el = this.wrapRef.current;

		if (!el) {
			return;
		}

		const { align = "left", minWidth = 280 } = this.props;
		const rect = el.getBoundingClientRect();
		const viewportHeight = window.innerHeight;
		const viewportWidth = window.innerWidth;
		const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_GUTTER;
		const spaceAbove = rect.top - VIEWPORT_GUTTER;

		// Prefer opening downwards. Flip upwards only when that gives the popover noticeably more room.
		const openUp = spaceBelow < MENU_MAX_H && spaceAbove > spaceBelow + 80;
		const available = Math.max(160, (openUp ? spaceAbove : spaceBelow) - 6);

		// Never wider than the screen, never taller than the space on the chosen side
		const width = Math.min(minWidth, viewportWidth - VIEWPORT_GUTTER * 2);
		const maxHeight = Math.min(MENU_MAX_H, available);
		const listMaxHeight = Math.max(ROW_H * 2, Math.min(ROW_H * LIST_ROWS, Math.floor((maxHeight - 120) / ROW_H) * ROW_H));

		const style = { position: "fixed", minWidth: width, maxWidth: viewportWidth - VIEWPORT_GUTTER * 2, maxHeight, zIndex: 9999 };

		if (align === "right") {
			style.right = Math.max(VIEWPORT_GUTTER, viewportWidth - rect.right);
		} else {
			style.left = Math.min(rect.left, Math.max(VIEWPORT_GUTTER, viewportWidth - width - VIEWPORT_GUTTER));
		}

		if (openUp) {
			style.bottom = viewportHeight - rect.top + 6;
		} else {
			style.top = rect.bottom + 6;
		}

		this.setState({ menuStyle: style, listMaxHeight, openUp });
	};

	onTriggerClick = event => {
		event.stopPropagation();

		if (this.props.open) {
			if (this.props.onClose) {
				this.props.onClose();
			}
		} else if (this.props.onOpen) {
			this.props.onOpen();
		}
	};

	onQueryChange = event => {
		this.setState({ query: event.target.value });
	};

	getSections() {
		const { sections, options } = this.props;
		const list = sections || [{ title: null, options: options || [] }];
		const query = this.state.query.trim().toLowerCase();

		return list.map(section => {
			const sectionOptions = section.options || [];

			return {
				...section,
				options: query ? sectionOptions.filter(option => String(option.label || "").toLowerCase().indexOf(query) !== -1) : sectionOptions
			};
		});
	}

	renderOption = option => {
		const { single, onToggle } = this.props;
		const checked = !!option.checked;

		return (
			<button
				type="button"
				key={option.id}
				className={clsx("filter-chip__opt", { "filter-chip__opt--on": checked && single })}
				onClick={event => {
					event.stopPropagation();

					if (onToggle) {
						onToggle(option);
					}
				}}
			>
				{single ? (
					<span className={clsx("filter-chip__radio", { "filter-chip__radio--on": checked })} />
				) : (
					<span className={clsx("filter-chip__cb", { "filter-chip__cb--on": checked })}>
						<Icon.Check size={11} strokeWidth={3.5} />
					</span>
				)}
				{option.tone && <span className={clsx("filter-chip__dot", `filter-chip__dot--${option.tone}`)} />}
				{option.avatar && <span className="filter-chip__av">{option.avatar}</span>}
				<span className="filter-chip__t">{option.label}</span>
				{typeof option.meta !== "undefined" && option.meta !== null && <span className="filter-chip__m">{option.meta}</span>}
			</button>
		);
	};

	renderMenu() {
		const { title, searchable, searchPlaceholder = "Search…", single, onSelectAll, onClear, onClose, extra, menuClassName } = this.props;
		const { query, menuStyle, listMaxHeight, openUp } = this.state;

		if (!menuStyle) {
			return null;
		}

		const total = this.getSections().reduce((sum, section) => sum + section.options.length, 0);
		const showFooter = !single && (onSelectAll || onClear || onClose);

		// Keep the popover light with huge lists: render the first rows, the search box narrows the rest
		let budget = MAX_RENDERED_OPTIONS;
		const sections = this.getSections().map(section => {
			const options = section.options.slice(0, Math.max(0, budget));
			budget -= options.length;
			return { ...section, options };
		});
		const hiddenCount = total - sections.reduce((sum, section) => sum + section.options.length, 0);

		return ReactDOM.createPortal(
			<div className={clsx("filter-chip__pop", { "filter-chip__pop--up": openUp }, menuClassName)} style={menuStyle} ref={el => (this._menuEl = el)} onClick={event => event.stopPropagation()}>
				{searchable && (
					<label className="filter-chip__search">
						<Icon.Search size={14} />
						<input type="search" value={query} onChange={this.onQueryChange} placeholder={searchPlaceholder} aria-label={searchPlaceholder} autoComplete="off" autoFocus />
					</label>
				)}
				{!searchable && title && <div className="filter-chip__h">{title}</div>}

				<div className="filter-chip__list" style={{ maxHeight: listMaxHeight }}>
					{sections.map((section, index) => (
						<React.Fragment key={section.title || index}>
							{section.title && section.options.length > 0 && <div className="filter-chip__h">{section.title}</div>}
							{section.options.map(this.renderOption)}
						</React.Fragment>
					))}

					{total === 0 && <div className="filter-chip__none">{query ? `Nothing matches "${query}"` : "Nothing to choose from"}</div>}
					{hiddenCount > 0 && <div className="filter-chip__none">{hiddenCount.toLocaleString()} more, type to narrow the list</div>}
				</div>

				{extra}

				{showFooter && (
					<div className="filter-chip__foot">
						{onSelectAll && (
							<button type="button" className="filter-chip__link" onClick={onSelectAll}>
								Select all
							</button>
						)}
						{onClear && (
							<button type="button" className="filter-chip__link" onClick={onClear}>
								Clear
							</button>
						)}
						<span className="filter-chip__foot__spacer" />
						<button type="button" className="filter-chip__done" onClick={onClose}>
							Done
						</button>
					</div>
				)}
			</div>,
			document.body
		);
	}

	render() {
		const { variant = "chip", tone, label, labelClassName, icon, count, active, open, className, title: triggerTitle } = this.props;

		const isChip = variant === "chip";
		const isPill = variant === "pill";

		return (
			<div className={clsx("filter-chip", className)} ref={this.wrapRef} onClick={event => event.stopPropagation()}>
				<button
					type="button"
					title={triggerTitle}
					className={clsx("filter-chip__trigger", {
						"filter-chip__trigger--chip": isChip,
						"filter-chip__trigger--button": variant === "button",
						"filter-chip__trigger--pill": isPill,
						[`filter-chip__trigger--${tone}`]: isPill && tone,
						"filter-chip__trigger--active": active,
						"filter-chip__trigger--open": open
					})}
					onClick={this.onTriggerClick}
					aria-expanded={open}
				>
					{isChip && !icon && (
						<span className="filter-chip__plus">
							<Icon.PlusCircle size={14} />
						</span>
					)}
					{icon && <span className="filter-chip__icon">{icon}</span>}
					<span className={clsx("filter-chip__label", labelClassName)}>{label}</span>
					{count > 0 && <span className="filter-chip__n">{count}</span>}
					{!isChip && <Icon.ChevronDown size={isPill ? 12 : 13} className="filter-chip__chev" />}
				</button>

				{open && this.renderMenu()}
			</div>
		);
	}
}

export default FilterChip;
